import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileText, Plane, Download, Eye, MoreHorizontal, Trash2, Send, Pencil, GripVertical, XCircle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, isValid } from "date-fns";
import { getDocumentTypeLabel } from "@/lib/documentNaming";

interface Contract {
  id: string;
  contract_number: string;
  storage_path?: string;
  status: string;
  created_at: string;
  event_details?: any;
}

interface DocumentsPanelProps {
  contracts: Contract[];
  onPreview: (storagePath: string, contractNumber: string) => void;
  onDownload: (storagePath: string, contractNumber: string) => void;
  onEdit: (contract: Contract) => void;
  onDelete: (contract: Contract) => void;
  onAttachToEmail: (contract: Contract) => void;
  onSendForSignature?: (contract: Contract) => void;
  onCancelContract?: (contract: Contract) => void;
}

// Export document data type for drag-and-drop
export interface DraggedDocument {
  id: string;
  contractNumber: string;
  storagePath: string;
  storageBucket: string;
  filename: string;
  contentType: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'signed': return 'bg-green-500/10 text-green-700 border-green-500/20';
    case 'sent': return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
    case 'viewed': return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
    case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'expired': return 'bg-muted text-muted-foreground border-muted';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getDocIcon = (contractNumber: string) => {
  if (contractNumber?.startsWith('Timeline-') || contractNumber?.startsWith('ITIN-')) {
    return <Plane className="h-3 w-3 text-primary" />;
  }
  if (contractNumber?.startsWith('TechRider-')) {
    return <FileText className="h-3 w-3 text-orange-500" />;
  }
  return <FileText className="h-3 w-3 text-amber-600" />;
};

const formatDocDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    if (isValid(date)) {
      return format(date, "M/d");
    }
  } catch {}
  return "";
};

// Helper to detect content type from file extension
const getContentTypeFromPath = (path: string): { contentType: string; extension: string } => {
  const extension = path.toLowerCase().split('.').pop() || '';
  const mimeTypes: Record<string, string> = {
    // Documents
    'pdf': 'application/pdf',
    'html': 'text/html',
    'htm': 'text/html',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text
    'txt': 'text/plain',
    'md': 'text/markdown',
    'json': 'application/json',
    'csv': 'text/csv',
    'xml': 'application/xml',
    'log': 'text/plain',
    'yaml': 'text/yaml',
    'yml': 'text/yaml',
    // Images
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'm4a': 'audio/mp4',
    'ogg': 'audio/ogg',
    'aac': 'audio/aac',
    'flac': 'audio/flac',
    // Video
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska',
  };
  return { contentType: mimeTypes[extension] || 'application/octet-stream', extension };
};

const handleDragStart = (e: React.DragEvent, doc: Contract) => {
  const docTypeLabel = getDocumentTypeLabel(doc.contract_number);
  const storagePath = doc.storage_path || '';
  const { contentType, extension } = getContentTypeFromPath(storagePath);
  
  // Use original filename from storage path for non-document types, otherwise use label
  let filename: string;
  const isDocument = ['pdf', 'html', 'htm'].includes(extension);
  if (isDocument) {
    filename = `${docTypeLabel}.${extension || 'pdf'}`;
  } else {
    filename = storagePath.split('/').pop() || `${docTypeLabel}.${extension}`;
  }
  
  const dragData: DraggedDocument = {
    id: doc.id,
    contractNumber: doc.contract_number,
    storagePath: storagePath,
    storageBucket: 'contracts',
    filename: filename,
    contentType: contentType,
  };
  e.dataTransfer.setData('application/json', JSON.stringify(dragData));
  e.dataTransfer.effectAllowed = 'copy';
};

export function DocumentsPanel({
  contracts,
  onPreview,
  onDownload,
  onEdit,
  onDelete,
  onAttachToEmail,
  onSendForSignature,
  onCancelContract,
}: DocumentsPanelProps) {
  if (!contracts || contracts.length === 0) {
    return (
      <div className="border rounded-lg p-3 bg-card">
        <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4" /> Documents
        </h4>
        <p className="text-xs text-muted-foreground text-center py-2">No documents yet</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 bg-card">
      <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4" /> Documents
        <Badge variant="secondary" className="text-[10px] h-4 px-1.5 ml-auto">
          {contracts.length}
        </Badge>
      </h4>
      
      <p className="text-[9px] text-muted-foreground mb-2 flex items-center gap-1">
        <GripVertical className="h-2.5 w-2.5" /> Drag to Email to attach
      </p>
      
      <ScrollArea className="h-[180px]">
        <div className="space-y-1.5">
          {contracts.map((doc) => {
            const docTypeLabel = getDocumentTypeLabel(doc.contract_number);
            const isItinerary = doc.contract_number?.startsWith('Timeline-') || doc.contract_number?.startsWith('ITIN-');
            const isAgreement = doc.contract_number?.startsWith('Agreement-') || doc.contract_number?.startsWith('LSC-');
            const canDrag = !!doc.storage_path;
            
            return (
              <div
                key={doc.id}
                draggable={canDrag}
                onDragStart={(e) => canDrag && handleDragStart(e, doc)}
                className={`flex items-center gap-2 p-1.5 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors group ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                {canDrag && <GripVertical className="h-3 w-3 text-muted-foreground/50 opacity-0 group-hover:opacity-100 shrink-0" />}
                {getDocIcon(doc.contract_number)}
                
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium truncate">{docTypeLabel}</p>
                  <p className="text-[9px] text-muted-foreground">
                    {formatDocDate(doc.created_at)}
                  </p>
                </div>
                
                <Badge 
                  variant="outline" 
                  className={`text-[8px] h-4 px-1 ${getStatusColor(doc.status)}`}
                >
                  {doc.status}
                </Badge>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    {doc.storage_path && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => onPreview(doc.storage_path!, doc.contract_number)}
                          className="text-xs"
                        >
                          <Eye className="h-3 w-3 mr-2" /> Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => onDownload(doc.storage_path!, doc.contract_number)}
                          className="text-xs"
                        >
                          <Download className="h-3 w-3 mr-2" /> Download
                        </DropdownMenuItem>
                      </>
                    )}
                    {!isItinerary && (
                      <DropdownMenuItem 
                        onClick={() => onEdit(doc)}
                        className="text-xs"
                      >
                        <Pencil className="h-3 w-3 mr-2" /> Edit
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => onAttachToEmail(doc)}
                      className="text-xs"
                    >
                      <Send className="h-3 w-3 mr-2" /> Attach to Email
                    </DropdownMenuItem>
                    {isAgreement && onSendForSignature && (doc.status === 'draft' || doc.status === 'cancelled' || doc.status === 'expired') && (
                      <DropdownMenuItem 
                        onClick={() => onSendForSignature(doc)}
                        className="text-xs"
                      >
                        <Send className="h-3 w-3 mr-2" /> {doc.status === 'draft' ? 'Send for Signature' : 'Resend for Signature'}
                      </DropdownMenuItem>
                    )}
                    {isAgreement && onCancelContract && (doc.status === 'sent' || doc.status === 'signed') && (
                      <DropdownMenuItem 
                        onClick={() => onCancelContract(doc)}
                        className="text-xs text-amber-600 focus:text-amber-600"
                      >
                        <XCircle className="h-3 w-3 mr-2" /> Cancel Contract
                      </DropdownMenuItem>
                    )}
                    {/* Prevent deletion of signed or paid contracts */}
                    {doc.status !== 'signed' && doc.status !== 'paid' && (
                      <DropdownMenuItem 
                        onClick={() => onDelete(doc)}
                        className="text-xs text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-3 w-3 mr-2" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
